import {
    Box,
    Flex,
    LoadingOverlay,
    Title
} from "@mantine/core";
import {
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_RowSelectionState,
    MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import React, { useState } from "react";

import { useDisclosure } from "@mantine/hooks";
import {
    IconFile,
    IconFolder,
    IconFolderOpen
} from "@tabler/icons-react";

import { modals } from "@mantine/modals";

import BreadCrumb from "../../_base/component/_layout/_breadcrumb";
import AppAction from "../../common/AppAction";
import { _localization } from "../../config/location";

import { CategoryModelTreeNew } from "../../model/TblCategoryModel";
import CreateViewMenu from "./CreateView";

type Props = {
  isModal?: boolean;
  setValueCategory?: any; // Hàm để cập nhật giá trị danh mục
};

const MenuContent = ({ isModal, setValueCategory }: Props) => {
  const hacomUrl = process.env.REACT_APP_BASE_WEB_HACOM_URL;
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<CategoryModelTreeNew[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
 

  const [visible, { toggle, close, open }] = useDisclosure(false);


  //   format tiền
  const formatCurrencyVND = (value: any) => {
    if (value == null) {
      return "";
    }
    // Định dạng số theo chuẩn Việt Nam
    return value.toLocaleString("vi-VN", {
      minimumFractionDigits: 0, // Không hiển thị phần thập phân
      maximumFractionDigits: 0, // Đảm bảo không có chữ số sau dấu phẩy
    });
  };

  const [searchValue, setSearchValue] = useState(""); // Giá trị tìm kiếm gốc


  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "STT",
        header: "STT",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const getFormattedIndex = (row: any): string => {
            let index = row.index + 1;
            let parent = row.getParentRow?.();
            let formatted = `${index}`;
            while (parent) {
              formatted = `${parent.index + 1}.${formatted}`;
              parent = parent.getParentRow?.();
            }
            return formatted;
          };

          const getColorByDepth = (depth: number) => {
            switch (depth) {
              case 0:
                return "#333"; // đen nhạt
              // case 1:
              //   return "#4c6ef5"; // xanh lam nhạt
              // case 2:
              //   return "#38d9a9"; // xanh lá nhạt
              default:
                return "#333";
            }
          };

          const getIconByDepth = (depth: number) => {
            const size = 16; // Kích thước nhỏ hơn
            switch (depth) {
              case 0:
                return <IconFolder size={size} color="#333" />; // icon cho cha
              case 1:
                return <IconFolderOpen size={size} color="#4c6ef5" />; // icon cho con cấp 1
              case 2:
                return <IconFile size={size} color="#38d9a9" />; // icon cho con cấp 2
              default:
                return <IconFile size={size} color="#f76707" />; // icon cho con cấp sâu hơn
            }
          };

          return (
            <Flex align="center" gap="xs">
              {getIconByDepth(row.depth)}
              <span
                style={{
                  color: getColorByDepth(row.depth),
                  fontWeight: 500,
                  fontSize: 14,
                }}
              >
                {getFormattedIndex(row)}
              </span>
            </Flex>
          );
        },
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: data || [],
    enableColumnResizing: true,
    enableTopToolbar: false,
    enableSubRowSelection: true,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,

    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: [""],
        right: ["action"],
      },
      // columnVisibility: { id: true },
      density: "xs",
    },
    enableExpanding: true,
    enableExpandAll: true, // Cho phép mở tất cả dòng
    paginateExpandedRows: true, // Phân trang cả dòng mở rộng
    enableStickyFooter: true,
    enableRowSelection: true,
    enableToolbarInternalActions: false,
    mantineTableContainerProps: {
      style: {
        maxHeight: height,
        minHeight: height,
        overflowX: "auto", // Cho phép cuộn ngang
      },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    enableColumnFilters: true,
    enableSorting: true,
    enableColumnActions: true,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    rowCount,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    mantineTableBodyCellProps: ({ row, cell }) => {
      const indentPx = row.depth * 20;
      const targetColumns = ["STT", "categoryName"]; // Các cột cần lùi

      return {
        style: {
          fontWeight: 550,
          fontSize: "12px",
          padding: "6px 8px",
          paddingLeft: targetColumns.includes(cell.column.id)
            ? `${indentPx + 4}px`
            : "4px",
        },
      };
    },

    mantineTableBodyRowProps: ({ row }) => ({
      style: {
        backgroundColor: row.depth === 0 ? "#f9f9f9" : "#fff", // Màu nền cho hàng cha và con
      },
    }),
    getRowId: (row) => row.id?.toString(),
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      sorting,
      rowSelection,
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "lg",
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    getSubRows: (row: any) => {
      if (!row.categoryChildModels || !Array.isArray(row.categoryChildModels)) {
        return []; // Trả về mảng rỗng nếu không có dữ liệu con
      }
      return row.categoryChildModels;
    },
  });

  const openModal = () =>
    modals.openConfirmModal({
      title: (
        <>
          <div color="white">
            <Title order={5}>Thêm mới nội dung menu!</Title>
          </div>
        </>
      ),
      children: <CreateViewMenu />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });

  return (
    <Box pos={"relative"}>
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />
      {!isModal && (
        <Flex
          gap="md"
          justify={"space-between"}
          align="center"
          direction="row"
          wrap="wrap"
          w={"100%"}
        >
          <BreadCrumb></BreadCrumb>
          <AppAction
            openModal={openModal}
            isUpdateSearchItem={true}
          ></AppAction>
        </Flex>
      )}
      <div
        style={{
          border: "1px solid #dee2e6",
          padding: "10px 10px",
          overflowX: "hidden",
          overflowY: "hidden",
        }}
        ref={headerRef}
      ></div>
      {/* <div className="mt-5" style={{ marginTop: "10px" }}>
        <MantineReactTable table={table} />
      </div> */}
    </Box>
  );
};

export default MenuContent;
